import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import type { TicketData } from '@/utilities/types/table'
import type { FilterState } from '@/utilities/types/filters'
import { sortByField } from '@/utilities/sorting'

dayjs.extend(isBetween)

export const filterWorker = () => {
  // Precompile dayjs instances for better performance
  const createDayjsCache = () => {
    const cache = new Map<string, dayjs.Dayjs>()
    return (dateStr: string) => {
      if (!cache.has(dateStr)) {
        cache.set(dateStr, dayjs(dateStr))
      }
      return cache.get(dateStr)!
    }
  }

  const getDayjs = createDayjsCache()

  self.addEventListener('message', (e: MessageEvent<{ data: TicketData[], filterState: FilterState }>) => {
    const { data, filterState } = e.data
    const batchSize = 1000
    let filtered = []
    
    // Process in batches to avoid blocking
    for (let i = 0; i < data.length; i += batchSize) {
      const batch = data.slice(i, i + batchSize).filter((row: TicketData) => {
        // Regular filters - early return optimization
        if (!checkRegularFilters(row, filterState.regularFilters)) return false
        if (!checkDateRanges(row, filterState.dateRanges, getDayjs)) return false
        if (!checkTimeRange(row, filterState.timeRange, getDayjs)) return false
        return true
      })
      filtered.push(...batch)
    }
    
    // Apply sorting
    if (filterState.sort) {
      filtered = sortByField(filtered, filterState.sort.field, filterState.sort.direction)
    }

    self.postMessage(filtered)
  })
}

function checkRegularFilters(row: TicketData, filters: Record<string, string[]>) {
  return Object.entries(filters).every(([field, values]) => {
    if (!values?.length) return true
    return values.includes(String(row[field as keyof TicketData]))
  })
}

function checkDateRanges(
  row: TicketData, 
  ranges: Record<string, { start: string | null, end: string | null }>,
  getDayjs: (date: string) => dayjs.Dayjs
) {
  return Object.entries(ranges).every(([field, range]) => {
    if (!range || (!range.start && !range.end)) return true
    
    const dateField = field.replace(' ', '') as keyof Pick<TicketData, 'statusDate' | 'modifiedDate'>
    const rowDate = row[dateField]
    if (!rowDate) return true
    
    const rowDayjs = getDayjs(rowDate)
    const startDate = range.start ? getDayjs(range.start) : null
    const endDate = range.end ? getDayjs(range.end) : null

    if (startDate && endDate) {
      return rowDayjs.isBetween(startDate, endDate, 'day', '[]')
    }
    return (!startDate || rowDayjs.isSameOrAfter(startDate, 'day')) &&
           (!endDate || rowDayjs.isSameOrBefore(endDate, 'day'))
  })
}

function checkTimeRange(
  row: TicketData,
  timeRange: { start: string | null, end: string | null } | null,
  getDayjs: (time: string) => dayjs.Dayjs
) {
  if (!timeRange || (!timeRange.start && !timeRange.end)) return true
  
  const rowTime = getDayjs(row.time)
  const startTime = timeRange.start ? getDayjs(timeRange.start) : null
  const endTime = timeRange.end ? getDayjs(timeRange.end) : null

  return (!startTime || rowTime.isSameOrAfter(startTime, 'minute')) &&
         (!endTime || rowTime.isSameOrBefore(endTime, 'minute'))
}